/**
 * Shopify CSV Generator
 * Converts SHOPLINE CSV data to Shopify import format
 */

const SHOPIFY_HEADERS = [
  'Handle', 'Title', 'Body (HTML)', 'Vendor', 'Type', 'Tags', 'Published',
  'Option1 Name', 'Option1 Value', 'Option2 Name', 'Option2 Value',
  'Option3 Name', 'Option3 Value', 'Variant SKU', 'Variant Grams',
  'Variant Inventory Tracker', 'Variant Inventory Qty', 'Variant Inventory Policy',
  'Variant Fulfillment Service', 'Variant Price', 'Variant Compare At Price',
  'Variant Requires Shipping', 'Variant Weight', 'Variant Weight Unit',
  'Product image URL', 'Image position', 'Image alt text', 'Gift Card',
  'SEO Title', 'SEO Description', 'Variant image URL', 'Status'
];

/**
 * Generate Shopify-compatible CSV from SHOPLINE data
 * @param {Array} shoplineData - Parsed SHOPLINE CSV data
 * @returns {string} - Shopify CSV string
 */
export function generateShopifyCsv(shoplineData) {
  if (!shoplineData?.length) {return '';}

  const rows = [];
  const grouped = groupByHandle(shoplineData);

  for (const product of grouped) {
    rows.push(...generateProductRows(product));
  }

  return buildCsvString(rows);
}

function groupByHandle(data) {
  const grouped = new Map();

  for (const row of data) {
    // 兼容 SHOPLINE 2.0 (Handle) 和 SHOPLINE 1.0 (handle)
    const handle = row['Handle'] || row['handle'] || '';
    if (!handle) {continue;}

    if (!grouped.has(handle)) {
      grouped.set(handle, {
        handle,
        mainRow: row,
        variants: []
      });
    }
    grouped.get(handle).variants.push(row);
  }

  return [...grouped.values()];
}

function generateProductRows(product) {
  const { mainRow, variants } = product;
  const images = collectImages(mainRow, variants);
  const rows = [];

  variants.forEach((variant, index) => {
    rows.push(buildVariantRow(mainRow, variant, images, index));
  });

  return rows;
}

function collectImages(mainRow, variants) {
  const images = new Set();

  // 辅助函数：添加图片（支持字符串、数组、逗号分隔）
  const addImage = (img) => {
    if (!img) return;
    const imgStr = String(img).trim();
    if (imgStr && (imgStr.startsWith('http://') || imgStr.startsWith('https://'))) {
      images.add(imgStr);
    }
  };

  const addImageList = (imgList) => {
    if (!imgList) return;
    if (Array.isArray(imgList)) {
      imgList.forEach(addImage);
    } else {
      String(imgList).split(',').forEach(addImage);
    }
  };

  // SHOPLINE 2.0 图片字段
  addImage(mainRow['Master image']);
  addImageList(mainRow['Additional image']);

  // SHOPLINE 1.0 图片字段 (images 是数组，Image 可能是字符串或数组)
  addImageList(mainRow['images']);
  addImageList(mainRow['Image']);
  addImage(mainRow['variant_image']);

  // 从所有变体中收集图片
  variants.forEach(v => {
    // SHOPLINE 2.0
    addImage(v['Master image']);
    addImage(v['Additional image']);
    addImage(v['SKU image']);

    // SHOPLINE 1.0
    addImageList(v['images']);
    addImageList(v['Image']);
    addImage(v['variant_image']);
  });

  return [...images];
}

function buildVariantRow(mainRow, variant, images, variantIndex) {
  const row = {};

  // Basic product info - 兼容 SHOPLINE 2.0 和 SHOPLINE 1.0 字段
  row['Handle'] = mainRow['Handle'] || mainRow['handle'] || '';
  row['Title'] = mainRow['Title*'] || mainRow['title'] || '';
  row['Body (HTML)'] = mainRow['Product description html'] || mainRow['description'] || '';
  row['Vendor'] = mainRow['Vendor'] || mainRow['brand'] || '';
  row['Type'] = mainRow['Product type'] || mainRow['collections']?.join(', ') || mainRow['Online Store Categories'] || '';
  row['Tags'] = mainRow['Tags'] || mainRow['tags']?.join(', ') || mainRow['Product Tag'] || '';

  // Published 状态 - 兼容多种格式
  const published = mainRow['Published'] || mainRow['published'];
  row['Published'] = (published === 'Y' || published === 'TRUE' || published === true) ? 'TRUE' : 'FALSE';

  // Variant options - 兼容两种格式
  row['Option1 Name'] = variant['Option1 Name'] || variant['option1_name'] || 'Title';
  row['Option1 Value'] = variant['Option1 Value'] || variant['option1_value'] || variant['Variant (English) (DO NOT EDIT)'] || '';
  row['Option2 Name'] = variant['Option2 Name'] || variant['option2_name'] || '';
  row['Option2 Value'] = variant['Option2 Value'] || variant['option2_value'] || '';
  row['Option3 Name'] = variant['Option3 Name'] || variant['option3_name'] || '';
  row['Option3 Value'] = variant['Option3 Value'] || variant['option3_value'] || '';

  // Variant details - 兼容 SHOPLINE 2.0 和 SHOPLINE 1.0
  row['Variant SKU'] = variant['skuid'] || variant['sku'] || variant['SKU'] || '';
  row['Variant Weight'] = variant['Variant weight'] || variant['weight'] || variant['Weight(KG)'] || '';
  row['Variant Weight Unit'] = variant['Variant weight unit'] || variant['weight_unit'] || 'kg';
  row['Variant Grams'] = convertToGrams(
    parseFloat(variant['Variant weight'] || variant['weight'] || variant['Weight(KG)'] || 0),
    variant['Variant weight unit'] || variant['weight_unit'] || 'kg'
  );
  row['Variant Inventory Qty'] = variant['SKU Inventory Quantity'] || variant['stock'] || variant['Quantity (DO NOT EDIT)'] || '0';
  row['Variant Inventory Tracker'] = 'shopify';
  row['Variant Inventory Policy'] = variant['Continue selling when out of stock'] === 'allow' ? 'continue' : 'deny';
  row['Variant Fulfillment Service'] = 'manual';
  row['Variant Price'] = variant['SKU price'] || variant['price'] || variant['Regular Price'] || '0';

  // Compare at price (原价/划线价)
  const comparePrice = variant['SKU original price'] || variant['compare_at_price'] || variant['Sale Price'];
  if (comparePrice && parseFloat(comparePrice) > 0) {
    row['Variant Compare At Price'] = comparePrice;
  }
  row['Variant Requires Shipping'] = 'TRUE';

  // Images - 使用 Shopify 官方字段名
  if (variantIndex === 0 && images.length > 0) {
    row['Product image URL'] = images[0];
    row['Image position'] = '1';
  }
  const variantImage = variant['SKU image'] || variant['variant_image'] || variant['Image'];
  if (variantImage) {
    row['Variant image URL'] = Array.isArray(variantImage) ? variantImage[0] : variantImage;
  }

  // SEO and other fields - 兼容两种格式
  row['SEO Title'] = mainRow['SEO title'] || mainRow['seo_title'] || mainRow['SEO Title (English)'] || '';
  row['SEO Description'] = mainRow['SEO description'] || mainRow['seo_description'] || mainRow['SEO Description (English)'] || '';
  row['Gift Card'] = 'FALSE';

  // Status mapping - 兼容多种状态格式
  const status = mainRow['Status'] || mainRow['status'];
  if (status === 'Y' || status === 'active' || status === 'published') {
    row['Status'] = 'active';
  } else if (status === 'draft' || status === 'hidden') {
    row['Status'] = 'draft';
  } else if (status === 'archived') {
    row['Status'] = 'archived';
  } else {
    row['Status'] = 'active';
  }

  return row;
}

function convertToGrams(weight, unit) {
  const u = (unit || 'kg').toLowerCase();
  if (u === 'kg' || u === 'kilogram' || u === 'kilograms') {return Math.round(weight * 1000);}
  if (u === 'g' || u === 'gram' || u === 'grams') {return Math.round(weight);}
  if (u === 'lb' || u === 'lbs' || u === 'pound' || u === 'pounds') {return Math.round(weight * 453.592);}
  if (u === 'oz' || u === 'ounce' || u === 'ounces') {return Math.round(weight * 28.3495);}
  return Math.round(weight * 1000);
}

function escapeCsvValue(value) {
  if (value === null || value === undefined) {return '';}
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function buildCsvString(rows) {
  if (!rows.length) {return '';}

  const lines = [];
  lines.push(SHOPIFY_HEADERS.join(','));

  for (const row of rows) {
    const values = SHOPIFY_HEADERS.map(h => escapeCsvValue(row[h] || ''));
    lines.push(values.join(','));
  }

  return lines.join('\n');
}
